import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Save,
  Trash2,
  Plus,
  X,
  Loader,
  AlertCircle,
  FileSignature,
  Copy,
  Upload,
  CheckCircle,
  FileText,
  Edit,
  Archive,
  Tag,
} from 'lucide-react';
import { auth, createAuditLog } from '../../lib/firebase';
import Modal from '../shared/Modal';
import PdfFieldEditor from './esign/PdfFieldEditor';
import {
  EsignFieldDefinition,
  SignerSlot,
  SigningMode,
  APPLICANT_SIGNER_SLOT_ID,
  normalizeSignerSlots,
  normalizeSigningMode,
} from '../../lib/esign/pdfFieldRenderer';
import { sanitizeSigningTemplateTags } from '../../lib/esign/signingTemplateTags';
import { isLegacyFirmaTemplate } from '../../lib/esign/legacySigningTemplate';
import { confirmDialog } from '../../lib/confirmStore';
import { notifyToast } from '../../lib/toastStore';

export interface NativeSigningTemplate {
  id: string;
  name: string;
  description?: string;
  pdfDataUrl?: string; // Base64 data URL or public URL of template PDF
  /** Storage-backed document (Phase B): bytes live in the bucket; resolve via GET /api/signing-templates/:id/document. */
  pdfStoragePath?: string | null;
  documentName?: string;
  fields: EsignFieldDefinition[];
  /** Multi-signer plan: signing mode + ordered signer slots (Applicant slot always present). */
  signingMode?: SigningMode;
  signerSlots?: SignerSlot[];
  tags?: string[];
  signerRoles?: string[];
  version?: number;
  status?: 'active' | 'archived';
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  // Legacy fields preserved for backward compatibility
  firmaTemplateId?: string;
  firmaTemplateName?: string;
  fieldMappings?: Record<string, any>;
}

interface SigningTemplateManagerProps {
  onClose: () => void;
  onSaved?: () => void;
  /** Render inline as a page section (E-Signatures page) instead of a modal. */
  embedded?: boolean;
}

export default function SigningTemplateManager({ onClose, onSaved, embedded = false }: SigningTemplateManagerProps) {
  const [templates, setTemplates] = useState<NativeSigningTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit / Create state
  const [editingId, setEditingId] = useState<string | null>(null); // null = creating
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPdfDataUrl, setEditPdfDataUrl] = useState<string | undefined>(undefined);
  const [editDocumentName, setEditDocumentName] = useState<string>('');
  const [editFields, setEditFields] = useState<EsignFieldDefinition[]>([]);
  const [editStatus, setEditStatus] = useState<'active' | 'archived'>('active');
  const [editSigningMode, setEditSigningMode] = useState<SigningMode>('any_order');
  const [editSignerSlots, setEditSignerSlots] = useState<SignerSlot[]>([{ id: APPLICANT_SIGNER_SLOT_ID, kind: 'applicant', label: 'Applicant' }]);
  const [newSlotLabel, setNewSlotLabel] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [activeTagFilter, setActiveTagFilter] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const authHeaders = async (): Promise<Record<string, string>> => {
    const token = await auth.currentUser?.getIdToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/signing-templates', { headers: await authHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const startCreate = () => {
    setEditingId('new');
    setEditName('');
    setEditDescription('');
    setEditPdfDataUrl(undefined);
    setEditDocumentName('');
    setEditFields([]);
    setEditStatus('active');
    setEditSigningMode('any_order');
    setEditSignerSlots([{ id: APPLICANT_SIGNER_SLOT_ID, kind: 'applicant', label: 'Applicant' }]);
    setNewSlotLabel('');
    setEditTags([]);
    setTagInput('');
    setSaveError(null);
  };

  const startEdit = async (tpl: NativeSigningTemplate) => {
    setEditingId(tpl.id);
    setEditName(tpl.name);
    setEditDescription(tpl.description || '');
    setEditPdfDataUrl(tpl.pdfDataUrl);
    setEditDocumentName(tpl.documentName || 'Document.pdf');
    setEditFields(Array.isArray(tpl.fields) ? tpl.fields : []);
    setEditStatus(tpl.status || 'active');
    setEditSigningMode(normalizeSigningMode(tpl.signingMode));
    setEditSignerSlots(normalizeSignerSlots(tpl.signerSlots));
    setNewSlotLabel('');
    setEditTags(sanitizeSigningTemplateTags(tpl.tags));
    setTagInput('');
    setSaveError(null);

    // Storage-backed template: the list carries only pdfStoragePath, so
    // resolve the document bytes on demand for the editor.
    if (!tpl.pdfDataUrl && tpl.pdfStoragePath) {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`/api/signing-templates/${tpl.id}/document`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.pdfDataUrl) setEditPdfDataUrl(data.pdfDataUrl);
        } else {
          setSaveError('Could not load the template document from storage. Field placement is unavailable until it loads.');
        }
      } catch {
        setSaveError('Could not load the template document from storage. Field placement is unavailable until it loads.');
      }
    }
  };

  const addTagFromInput = () => {
    const next = sanitizeSigningTemplateTags([...editTags, tagInput]);
    setEditTags(next);
    setTagInput('');
  };

  // ── Signer plan editing ─────────────────────────────────────────────
  // The Applicant slot is always present and can be reordered, never
  // removed. External slots are named roles (e.g. "Parent / Guardian");
  // the actual person is supplied by name + email at send time.
  const moveSlot = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= editSignerSlots.length) return;
    const next = [...editSignerSlots];
    [next[idx], next[j]] = [next[j], next[idx]];
    setEditSignerSlots(next);
  };

  const removeSlot = (id: string) => {
    if (id === APPLICANT_SIGNER_SLOT_ID) return;
    setEditSignerSlots(editSignerSlots.filter((s) => s.id !== id));
  };

  const renameSlot = (id: string, label: string) => {
    setEditSignerSlots(editSignerSlots.map((s) => (s.id === id ? { ...s, label } : s)));
  };

  const addSlot = () => {
    const label = newSlotLabel.trim();
    if (!label) return;
    const id = `slot_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    setEditSignerSlots([...editSignerSlots, { id, kind: 'external', label }]);
    setNewSlotLabel('');
  };

  const handleDuplicate = async (tpl: NativeSigningTemplate) => {
    if (!(await confirmDialog(`Duplicate the signing template "${tpl.name}"? A separate copy will be created that can be edited on its own.`))) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/signing-templates/${tpl.id}/duplicate`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error(await res.text());
      await loadTemplates();
      createAuditLog('SIGNING_TEMPLATE_DUPLICATED', tpl.id, `Duplicated template ${tpl.name}`).catch(() => {});
    } catch (err: any) {
      notifyToast('error', 'Duplicate failed: ' + err.message);
    }
  };

  const handlePdfFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
      notifyToast('warning', 'Please upload a valid PDF document.');
      return;
    }

    setEditDocumentName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const result = evt.target?.result as string;
      setEditPdfDataUrl(result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!editName.trim()) {
      setSaveError('Template name is required.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const body = {
        name: editName.trim(),
        description: editDescription.trim(),
        pdfDataUrl: editPdfDataUrl,
        documentName: editDocumentName,
        fields: editFields,
        signingMode: editSigningMode,
        signerSlots: normalizeSignerSlots(editSignerSlots),
        status: editStatus,
        tags: sanitizeSigningTemplateTags(editTags),
      };

      const headers = await authHeaders();

      if (editingId && editingId !== 'new') {
        const res = await fetch(`/api/signing-templates/${editingId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(await res.text());
      } else {
        const res = await fetch('/api/signing-templates', {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(await res.text());
      }

      createAuditLog('SIGNING_TEMPLATE_SAVED', editingId || 'new', `Saved signing template "${editName}"`).catch(() => {});
      await loadTemplates();
      onSaved?.();
      setEditingId(null);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const tpl = templates.find((t) => t.id === id);
    if (!(await confirmDialog(`Delete signing template "${tpl?.name || id}"? This cannot be undone.`))) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/signing-templates/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await res.text());
      createAuditLog('SIGNING_TEMPLATE_DELETED', id, 'Deleted signing template').catch(() => {});
      await loadTemplates();
    } catch (err: any) {
      notifyToast('error', 'Delete failed: ' + err.message);
    }
  };

  const visibleTemplates = activeTagFilter
    ? templates.filter((t) =>
        sanitizeSigningTemplateTags(t.tags).some((tag) => tag.toLowerCase() === activeTagFilter.toLowerCase())
      )
    : templates;

  const panel = (
      <div
        className={
          embedded
            ? 'bg-white rounded-2xl border border-slate-200 shadow-sm w-full flex flex-col overflow-hidden'
            : 'bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden'
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0 bg-slate-50">
          <div className="flex items-center space-x-2">
            <FileSignature className="h-5 w-5 text-brand" />
            <div>
              <h2 className="text-base font-extrabold text-slate-800">Signing Template Manager</h2>
              <p className="text-caption text-slate-500">Configure reusable document templates & field layouts</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {!editingId && (
              <button
                onClick={startCreate}
                className="px-3.5 py-1.5 btn-primary text-white text-caption font-bold rounded-xl flex items-center space-x-1 cursor-pointer transition-all shadow-sm"
              >
                <Plus className="h-4 w-4" />
                <span>New Template</span>
              </button>
            )}
            {!embedded && (
              <button aria-label="Close" title="Close"
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Content Body */}
        <div className={embedded ? 'p-6' : 'flex-1 overflow-y-auto p-6'}>
          {error && (
            <div className="flex items-start space-x-2 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 mb-4">
              <AlertCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
              <p className="text-caption text-rose-700">{error}</p>
            </div>
          )}

          {/* Edit / Create Form */}
          {editingId !== null && (
            <div className="border border-slate-200 rounded-2xl p-5 mb-6 bg-white shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-sm font-bold text-slate-800">
                  {editingId === 'new' ? 'Create New Signing Template' : `Edit Template: ${editName}`}
                </h3>
                <button
                  onClick={() => setEditingId(null)}
                  className="text-caption text-slate-400 hover:text-slate-600 font-semibold cursor-pointer"
                >
                  Cancel
                </button>
              </div>

              {saveError && (
                <div className="flex items-start space-x-2 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
                  <AlertCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                  <p className="text-caption text-rose-700">{saveError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <label className="block text-caption font-bold text-slate-600 mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:outline-none"
                    placeholder="e.g., J-1 Exchange Agreement"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-caption font-bold text-slate-600 mb-1">Description</label>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:outline-none"
                    placeholder="Optional description"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-caption font-bold text-slate-600 mb-1">Upload Base PDF Document</label>
                  <div className="flex items-center gap-2">
                    <label className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl border border-slate-200 cursor-pointer flex items-center gap-1.5 transition">
                      <Upload className="h-3.5 w-3.5 text-brand" />
                      <span>{editDocumentName ? 'Replace PDF' : 'Upload PDF'}</span>
                      <input type="file" accept="application/pdf" onChange={handlePdfFileUpload} className="hidden" />
                    </label>
                    {editDocumentName && (
                      <span className="text-caption text-emerald-600 font-semibold flex items-center gap-1 truncate">
                        <CheckCircle className="h-3.5 w-3.5" />
                        <span className="truncate max-w-[120px]">{editDocumentName}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-caption font-bold text-slate-600 mb-1">Tags</label>
                <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-2 border border-slate-200 rounded-xl bg-white focus-within:ring-2 focus-within:ring-brand/20">
                  {editTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold bg-brand/10 text-brand rounded-full border border-brand/20"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => setEditTags(editTags.filter((t) => t !== tag))}
                        className="text-brand/60 hover:text-brand cursor-pointer"
                        title={`Remove tag ${tag}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addTagFromInput();
                      }
                    }}
                    onBlur={addTagFromInput}
                    placeholder={editTags.length === 0 ? 'Add a tag and press Enter' : 'Add another…'}
                    className="flex-1 min-w-[140px] text-xs py-0.5 focus:outline-none focus:ring-[3px] focus:ring-brand-primary/15"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Up to 12 tags, 32 characters each. Tags help group templates — click a tag on any template card to filter the list.</p>
              </div>

              {/* Signing order & signers (multi-signer plan) */}
              <div className="border border-slate-200 rounded-xl p-3.5 bg-slate-50 space-y-3">
                <div>
                  <label className="block text-caption font-bold text-slate-600 mb-1">Signing Order &amp; Signers</label>
                  <p className="text-[11px] text-slate-500">
                    The Applicant is always a signer — move them in the order, but they cannot be removed. Add named slots for
                    anyone else who must sign (a parent, a sponsor, a witness); the person&apos;s name and email are entered when
                    the request is sent.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      { value: 'any_order', title: 'Any order', hint: 'Everyone is invited at once and can sign in any sequence' },
                      { value: 'strict_order', title: 'Strict order', hint: 'Signers sign one after another, in the order below' },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setEditSigningMode(opt.value)}
                      className={`flex-1 min-w-[220px] text-left px-3 py-2 rounded-xl border cursor-pointer transition ${
                        editSigningMode === opt.value
                          ? 'bg-brand/10 border-brand text-slate-800'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-brand/40'
                      }`}
                    >
                      <span className="block text-xs font-bold">{opt.title}</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">{opt.hint}</span>
                    </button>
                  ))}
                </div>
                <div className="space-y-1.5">
                  {editSignerSlots.map((slot, idx) => (
                    <div
                      key={slot.id}
                      className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2"
                    >
                      <span className="text-[11px] font-mono font-bold text-slate-400 w-5 text-center">{idx + 1}</span>
                      {slot.kind === 'applicant' ? (
                        <span className="flex-1 text-xs font-bold text-slate-800">
                          Applicant
                          <span className="ml-2 px-1.5 py-0.5 text-[11px] font-bold bg-brand/10 text-brand rounded-full border border-brand/20">
                            ALWAYS INCLUDED
                          </span>
                        </span>
                      ) : (
                        <input
                          type="text"
                          value={slot.label}
                          onChange={(e) => renameSlot(slot.id, e.target.value)}
                          className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:outline-none"
                          placeholder="Signer slot name, e.g. Parent / Guardian"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => moveSlot(idx, -1)}
                        disabled={idx === 0}
                        className="px-2 py-1 text-[11px] font-bold text-slate-500 hover:text-brand disabled:opacity-30 cursor-pointer"
                        title="Move earlier in the order"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSlot(idx, 1)}
                        disabled={idx === editSignerSlots.length - 1}
                        className="px-2 py-1 text-[11px] font-bold text-slate-500 hover:text-brand disabled:opacity-30 cursor-pointer"
                        title="Move later in the order"
                      >
                        ↓
                      </button>
                      {slot.kind !== 'applicant' && (
                        <button
                          type="button"
                          onClick={() => removeSlot(slot.id)}
                          className="px-2 py-1 text-[11px] font-bold text-rose-500 hover:text-rose-700 cursor-pointer"
                          title="Remove this signer slot"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newSlotLabel}
                    onChange={(e) => setNewSlotLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSlot();
                      }
                    }}
                    placeholder="Add a signer slot, e.g. Parent / Guardian"
                    className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-brand/20 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={addSlot}
                    disabled={!newSlotLabel.trim()}
                    className="px-3 py-2 bg-white border border-slate-200 text-caption font-bold rounded-xl text-slate-600 hover:bg-slate-100 transition flex items-center gap-1 cursor-pointer disabled:opacity-40"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Add Signer</span>
                  </button>
                </div>
              </div>

              {/* Interactive PDF Field Editor */}
              <div className="pt-2">
                <h4 className="text-caption font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Interactive Document Field Layout
                </h4>
                <PdfFieldEditor
                  pdfUrl={editPdfDataUrl}
                  fields={editFields}
                  onChange={setEditFields}
                  selectedSignerRole="applicant"
                  signerSlots={editSignerSlots}
                />
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  className="px-3.5 py-2 border border-slate-200 text-caption font-bold rounded-xl text-slate-600 hover:bg-slate-50 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !editName.trim()}
                  className="px-5 py-2 btn-primary text-white text-caption font-bold rounded-xl transition-all cursor-pointer flex items-center space-x-2 shadow-sm disabled:opacity-50"
                >
                  {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  <span>{saving ? 'Saving...' : 'Save Template'}</span>
                </button>
              </div>
            </div>
          )}

          {/* Template List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-6 w-6 animate-spin text-brand" />
            </div>
          ) : visibleTemplates.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 border border-slate-200 rounded-2xl">
              <FileSignature className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-bold">
                {activeTagFilter ? 'No templates carry this tag' : 'No signing templates found'}
              </p>
              <p className="text-caption text-slate-400 mt-1">
                {activeTagFilter
                  ? 'Clear the tag filter to see every template.'
                  : 'Upload PDFs and configure field coordinates for seamless document signing.'}
              </p>
              <button
                onClick={startCreate}
                className="mt-4 px-4 py-2 btn-primary text-white text-caption font-bold rounded-xl cursor-pointer transition-all flex items-center space-x-1.5 mx-auto"
              >
                <Plus className="h-4 w-4" />
                <span>Create First Template</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {activeTagFilter && (
                <div className="flex items-center gap-2 bg-brand/5 border border-brand/20 rounded-xl px-3 py-2">
                  <Tag className="h-3.5 w-3.5 text-brand" />
                  <span className="text-caption font-semibold text-slate-700">
                    Filtered by tag <strong>{activeTagFilter}</strong> — {visibleTemplates.length} of {templates.length} templates
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveTagFilter(null)}
                    className="ml-auto text-caption font-bold text-brand hover:underline cursor-pointer"
                  >
                    Clear filter
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-caption font-bold text-slate-400 uppercase tracking-wider">
                  {templates.length} Saved Template{templates.length !== 1 ? 's' : ''}
                </p>
              </div>

              {visibleTemplates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="border border-slate-200 rounded-xl p-4 bg-white hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-brand shrink-0" />
                        <h4 className="text-sm font-bold text-slate-800 truncate">{tpl.name}</h4>
                        {tpl.status === 'archived' && (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-slate-100 text-slate-500 rounded-full border border-slate-200">
                            Archived
                          </span>
                        )}
                        {isLegacyFirmaTemplate(tpl) && (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-50 text-amber-700 rounded-full border border-amber-200">
                            Legacy (Firma)
                          </span>
                        )}
                      </div>
                      {tpl.description && <p className="text-caption text-slate-500 mt-0.5 ml-6">{tpl.description}</p>}

                      <div className="flex items-center space-x-4 mt-2 ml-6 text-caption text-slate-400">
                        <span>
                          <strong>{Array.isArray(tpl.fields) ? tpl.fields.length : 0}</strong> configured fields
                        </span>
                        {Array.isArray(tpl.signerSlots) && tpl.signerSlots.length > 1 && (
                          <span>
                            <strong>{tpl.signerSlots.length}</strong> signers ·{' '}
                            {tpl.signingMode === 'strict_order' ? 'Strict order' : 'Any order'}
                          </span>
                        )}
                        {tpl.documentName && <span>Document: {tpl.documentName}</span>}
                      </div>

                      {isLegacyFirmaTemplate(tpl) && (
                        <p className="text-caption text-amber-600 mt-1.5 ml-6">
                          Legacy Firma template — it has no native document and cannot be sent with the current signing system.
                        </p>
                      )}

                      {Array.isArray(tpl.tags) && tpl.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2 ml-6">
                          {tpl.tags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => setActiveTagFilter(tag)}
                              title="Filter by this tag"
                              className={`px-2 py-0.5 text-[10px] font-bold rounded-full border cursor-pointer transition ${
                                activeTagFilter === tag
                                  ? 'bg-brand text-white border-brand'
                                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-brand/50 hover:text-brand'
                              }`}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-1 shrink-0 ml-3">
                      <button
                        onClick={() => startEdit(tpl)}
                        className="p-1.5 text-slate-400 hover:text-brand bg-slate-50 hover:bg-brand/10 rounded-lg cursor-pointer transition-all"
                        title="Edit Template"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(tpl)}
                        className="p-1.5 text-slate-400 hover:text-emerald-600 bg-slate-50 hover:bg-emerald-50 rounded-lg cursor-pointer transition-all"
                        title="Duplicate Template"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tpl.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 bg-slate-50 hover:bg-rose-50 rounded-lg cursor-pointer transition-all"
                        title="Delete Template"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
  );

  if (embedded) return panel;

  return ReactDOM.createPortal(
    <Modal onClose={onClose} ariaLabel="Signing Template Manager">
      {panel}
    </Modal>,
    document.body
  );
}
